import {
    type IntervalNotificationSchedule,
    NotificationFrequency,
    NotificationFrequencyMap,
    type NotificationSchedule,
    type WeeklyNotificationSchedule,
} from "@qendo/notifications";
import { EmptyWeek, type ScheduleTimeData, type WeekdayData } from "@qendo/schedule";
import { useState } from "react";
import { StyleSheet } from "react-native";
import { Button } from "~/components/Button";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { FieldGroup } from "~/components/form/FieldGroup";
import { SwitchField } from "~/components/form/SwitchField";
import { IntervalSelector } from "~/components/form/schedule/IntervalSelector";
import { ScheduleTimeSelector } from "~/components/form/schedule/ScheduleTimeSelector";
import { WeekdaySelector } from "~/components/form/schedule/WeekdaySelector";
import { EditListingItem } from "~/components/layout/EditListingItem";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { Subtitle } from "~/components/layout/text";
import { Only } from "~/components/permission/Only";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { formatInterval, formatTimes, formatWeekdayString } from "~/util/qendo/schedule";

export type NotificationScheduleFieldProps<
    Frequency extends NotificationFrequency,
    Allowed extends Frequency[] = Frequency[],
> = {
    label: string;
    value: NotificationSchedule | false;
    onUpdateSchedule: (schedule: NotificationSchedule<Frequency> | false) => void;
    fresh: NotificationSchedule<Frequency>;
    allowedFrequencies?: Allowed;
};

const ALL_FREQUENCIES = Object.values(NotificationFrequency);

export const NotificationScheduleField = <
    Frequency extends NotificationFrequency = NotificationFrequency,
    Allowed extends Frequency[] = Frequency[],
>({
    label,
    value,
    onUpdateSchedule,
    fresh,
    allowedFrequencies,
}: NotificationScheduleFieldProps<Frequency, Allowed>) => {
    const { background } = useTheme();
    const [updating, setUpdating] = useState(false);
    const [frequency, setFrequency] = useState<NotificationFrequency>(value ? value.frequency : fresh.frequency);
    const [days, setDays] = useState<WeekdayData>(EmptyWeek);
    const [interval, setInterval] = useState<number>(2);
    const [times, setTimes] = useState<ScheduleTimeData[]>([]);
    const allowed = filled(allowedFrequencies) ? allowedFrequencies : (ALL_FREQUENCIES as Allowed);

    const startUpdating = () => {
        const frequency = value ? value.frequency : fresh.frequency;
        setFrequency(allowed.includes(frequency as Frequency) ? frequency : (allowed[0] as Frequency));

        setDays({
            ...EmptyWeek,
            ...(value && Object.hasOwn(value, "days")
                ? (value as WeeklyNotificationSchedule).days
                : Object.hasOwn(fresh, "days")
                  ? (fresh as WeeklyNotificationSchedule).days
                  : {}),
        });
        setInterval(
            value && Object.hasOwn(value, "interval")
                ? (value as IntervalNotificationSchedule).interval
                : Object.hasOwn(fresh, "interval")
                  ? (fresh as IntervalNotificationSchedule).interval
                  : 2,
        );
        setTimes(value ? value.times : fresh.times);
        setUpdating(true);
    };

    const saveUpdates = () => {
        const selected = (allowed.includes(frequency as Frequency) ? frequency : allowed[0]) as Frequency;

        const payload = {
            frequency: selected,
            days: selected === NotificationFrequency.specific_days ? days : undefined,
            interval: selected === NotificationFrequency.interval ? interval : undefined,
            times,
        } as NotificationSchedule<Frequency>;

        onUpdateSchedule(payload);
        setUpdating(false);
    };

    return (
        <>
            <SwitchField
                label={label}
                value={Boolean(value)}
                onValueChange={(value) => onUpdateSchedule(value && fresh)}
            />
            {value && (
                <EditListingItem onPress={startUpdating}>
                    <Subtitle colour="primary">{NotificationFrequencyMap[value.frequency].label}</Subtitle>
                    {value.frequency === NotificationFrequency.specific_days && (
                        <Subtitle colour="primary">
                            {formatWeekdayString((value as WeeklyNotificationSchedule).days)}
                        </Subtitle>
                    )}
                    {value.frequency === NotificationFrequency.interval && (
                        <Subtitle colour="primary">
                            {formatInterval((value as IntervalNotificationSchedule).interval)}
                        </Subtitle>
                    )}
                    {value.times.length > 0 && <Subtitle colour="primary">{formatTimes(value.times)}</Subtitle>}
                </EditListingItem>
            )}
            {updating && (
                <FullPageModal
                    cancelable
                    title={`${label} reminder`}
                    onRequestClose={() => setUpdating(false)}
                    headerRight={() => <Button title="Save" onPress={saveUpdates} variant="secondary" size="sm" />}
                >
                    <Only when={allowed.length > 1}>
                        <ComboBox<NotificationFrequency, Frequency>
                            items={allowed}
                            itemMap={NotificationFrequencyMap}
                            value={frequency}
                            onItemSelected={setFrequency}
                            label="How often?"
                        />
                    </Only>
                    {frequency === NotificationFrequency.interval && (
                        <IntervalSelector value={interval} onChange={setInterval} />
                    )}
                    <FieldGroup style={[styles.time, background.secondary]} title="At what time?">
                        {frequency === NotificationFrequency.specific_days && (
                            <WeekdaySelector value={days} onChange={setDays} />
                        )}
                        <ScheduleTimeSelector times={times} onChange={setTimes} />
                    </FieldGroup>
                </FullPageModal>
            )}
        </>
    );
};

const styles = StyleSheet.create({
    time: {
        gap: 8,
    },
});
